'use client';

import type { PaperShaderElement } from '@paper-design/shaders';
import { Heatmap, LiquidMetal } from '@paper-design/shaders-react';
import React, { Component, type ErrorInfo, type ReactNode, Suspense, useCallback, useEffect, useRef } from 'react';
import DigitalCardLayerStyled from 'src/components/_common/DigitalCard/_components/DigitalCardLayer/index.styled';
import type { ShaderLayer } from 'src/components/_common/DigitalCard/_components/DigitalCardLayer/types';
import { useDigitalCardActions } from 'src/components/_common/DigitalCard/_hooks/useDigitalCard';
import styled from 'styled-components';

/**
 * [셰이더 컴포넌트 맵]
 *
 * @description
 * @paper-design/shaders-react 패키지의 셰이더 컴포넌트를 매핑합니다.
 * 새로운 셰이더 추가 시 이 맵에 등록해야 합니다.
 */
const SHADER_COMPONENTS = {
  liquidMetal: LiquidMetal,
  heatmap: Heatmap,
} as const;

export type ShaderType = keyof typeof SHADER_COMPONENTS;

/**
 * [셰이더 에러 바운더리]
 *
 * @description
 * WebGL 셰이더 렌더링 실패 시 에러를 캐치하여 카드 전체 크래시를 방지합니다.
 * 에러 발생 시 null을 렌더링하여 셰이더 레이어만 조용히 사라집니다.
 */
class ShaderErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ShaderLayer] 셰이더 렌더링 실패:', error, info);
  }

  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

interface ShaderContentProps {
  idx: number;
  shaderType: ShaderLayer['shaderType'];
  shaderProps: ShaderLayer['shaderProps'];
}

/**
 * [셰이더 렌더러]
 *
 * suspendWhenProcessingImage로 이미지 처리 완료 후에만 mount됩니다.
 * mount 후 paperShaderMount 세팅을 감지하여 onReady 콜백을 호출합니다.
 */
const ShaderRenderer = ({
  shaderType,
  shaderProps,
  onReady,
}: {
  shaderType: ShaderLayer['shaderType'];
  shaderProps: ShaderLayer['shaderProps'];
  onReady: () => void;
}) => {
  const shaderRef = useRef<PaperShaderElement>(null);

  useEffect(() => {
    let rafId: number;

    const checkShaderReady = () => {
      if (shaderRef.current?.paperShaderMount) {
        onReady();
        return;
      }
      rafId = requestAnimationFrame(checkShaderReady);
    };

    rafId = requestAnimationFrame(checkShaderReady);
    return () => cancelAnimationFrame(rafId);
  }, [onReady]);

  const ShaderComponent = SHADER_COMPONENTS[shaderType] as React.ComponentType<
    { style: React.CSSProperties; ref?: React.Ref<PaperShaderElement>; suspendWhenProcessingImage?: boolean } & Record<
      string,
      unknown
    >
  >;

  return (
    <ShaderComponent
      ref={shaderRef}
      style={{ width: '100%', height: '100%' }}
      suspendWhenProcessingImage
      {...shaderProps}
    />
  );
};

const ShaderContent = ({ idx, shaderType, shaderProps }: ShaderContentProps) => {
  const { handleResourceLoadComplete } = useDigitalCardActions();

  const handleShaderReady = useCallback(() => {
    handleResourceLoadComplete(`shader:${idx}`);
  }, [handleResourceLoadComplete, idx]);

  return (
    <ShaderContentWrapper>
      <Suspense fallback={null}>
        <ShaderErrorBoundary>
          <ShaderRenderer shaderType={shaderType} shaderProps={shaderProps} onReady={handleShaderReady} />
        </ShaderErrorBoundary>
      </Suspense>
    </ShaderContentWrapper>
  );
};

/**
 * [디지털 카드 셰이더 레이어 컴포넌트]
 *
 * @description
 * WebGL 기반 셰이더 효과를 디지털 카드에 표시하는 컴포넌트입니다.
 * suspendWhenProcessingImage로 셰이더 로드 완료를 감지하고, useAllResourceLoad와 통합합니다.
 */
export const DigitalCardShaderLayer = ({ idx, zIndex, translateZ, borderRadius, shaderType, shaderProps }: ShaderLayer) => {
  if (!SHADER_COMPONENTS[shaderType]) {
    console.warn(`Unknown shader type: ${shaderType}`);
    return null;
  }

  return (
    <ShaderLayerWrapper $zIndex={zIndex} $translateZ={translateZ} $borderRadius={borderRadius}>
      <ShaderContent idx={idx} shaderType={shaderType} shaderProps={shaderProps} />
    </ShaderLayerWrapper>
  );
};

const ShaderContentWrapper = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
`;

const ShaderLayerWrapper = styled(DigitalCardLayerStyled.BaseLayerWrapper)`
  overflow: ${({ $borderRadius }) => (!$borderRadius ? 'visible' : 'hidden')};
  ${({ $borderRadius }) => ($borderRadius ? `border-radius: ${$borderRadius}px;` : '')}
`;
